import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button, Sprite } from '@agissc/ktag-map';
import useAppState from '../../AppStore';
import { AuthenticatedUserRequired } from '../../utils/auth/AuthenticatedUserRequired';
import Scrollable from '../../components/scrollable/Scrollable';
import ConfigurationPlaceholder from './ConfigurationPlaceholder';
import { AccordionName } from '../../UIStateSlice';
import AccordionToggle from '../../components/accordion/AccordionToggle';
import { SortableList } from '../../components/sortableList/SortableList';
import { ConfigurationAccordionItem } from './ConfigurationAccordionItem';
import { useMapConfigurations } from './api/useMapConfigurations';

const ConfigurationsWithUserLoggedIn = () => {
  const { t } = useTranslation('agisviewer');

  const {
    configurations,
    isLoading,
    error,
    selectedConfiguration,
    onApplyConfiguration,
    onAddConfiguration,
    onUpdateConfiguration,
    onRemoveConfiguration,
    onUpdateOrder,
  } = useMapConfigurations();

  const setAllAccordionItems = useAppState((state) => state.setAllAccordionItems);

  const onToggleAccordionItems = (expanded: boolean) => {
    const ids = configurations?.map((c) => c.id) || [];
    setAllAccordionItems(AccordionName.MAP_CONFIGURATION, ids, expanded);
  };

  if (error) {
    return <div className="configuration__subtitle">{t('configuration_error')}</div>;
  }

  const addButtonTitle = selectedConfiguration
    ? t('configuration_add_disabled', { name: selectedConfiguration })
    : '';

  const renderConfigurationList = () => {
    if (!configurations || configurations.length === 0) {
      return (
        <div className="configuration__subtitle">
          {t('configuraton_list_title_empty')}
        </div>
      );
    }

    return (
      <Scrollable>
        <AccordionToggle
          onOpen={() => onToggleAccordionItems(true)}
          onClose={() => onToggleAccordionItems(false)}
          tooltipOpen={t('accordion_my_maps_expand_all')}
          tooltipClose={t('accordion_my_maps_collapse_all')}
          additionalClassNames="configuration__accordion-toggle"
        />

        <SortableList
          items={configurations}
          onOrderChange={onUpdateOrder}
          renderItem={(configuration, isDragOverlay) => {
            const isSelected = configuration.name === selectedConfiguration;

            return (
              <ConfigurationAccordionItem
                configuration={configuration}
                isSelected={isSelected}
                isDragOverlay={isDragOverlay ?? false}
                onApplyConfiguration={() => onApplyConfiguration(configuration)}
                onUpdateConfiguration={onUpdateConfiguration}
                onRemoveConfiguration={() => onRemoveConfiguration(configuration)}
              />
            );
          }}
        />
      </Scrollable>
    );
  };

  return (
    <>
      <div className="tooldialog__wrapper-content">
        <Button
          additionalClassNames="button__max-width button__with-border button__primary"
          title={addButtonTitle}
          disabled={!!selectedConfiguration || isLoading}
          onClick={onAddConfiguration}
        >
          {t('configuration_save')}
        </Button>
      </div>

      {isLoading && <ConfigurationPlaceholder />}

      {renderConfigurationList()}
    </>
  );
};

const LoginRequiredMessage = () => {
  return (
    <div className="configuration__subtitle">
      <p>
        <Sprite id="i-circle" additionalClassNames="configuration__icon" />
        <Trans ns="agisviewer" i18nKey="config_wrapper" />
      </p>
    </div>
  );
};

const Configurations = () => {
  return (
    <div className="tooldialog__wrapper">
      <AuthenticatedUserRequired errorHandler={LoginRequiredMessage}>
        <ConfigurationsWithUserLoggedIn />
      </AuthenticatedUserRequired>
    </div>
  );
};

export default Configurations;
